import { normalize } from "normalizr";

import {
    defaultErrorHandlers,
    noOpErrorHandlers,
    requestWithAuth,
    requestWithoutAuth
} from "../../utils/request";
import { getApiUrl } from "../../helpers/api";
import { tokenSchema, userSchema } from "../../schemas";

import * as session from "../session/actions";

// ----------------------------------------------------------------------------

export const FETCH_USER_INVITATION_TOKEN_REQUEST =
    "FETCH_USER_INVITATION_TOKEN_REQUEST";
export const FETCH_USER_INVITATION_TOKEN_SUCCESS =
    "FETCH_USER_INVITATION_TOKEN_SUCCESS";
export const FETCH_USER_INVITATION_TOKEN_FAILURE =
    "FETCH_USER_INVITATION_TOKEN_FAILURE";

function fetchUserInvitationRequest(tokenId) {
    return {
        type: FETCH_USER_INVITATION_TOKEN_REQUEST,
        tokenId
    };
}

function fetchUserInvitationSuccess(tokenId, entities) {
    return {
        type: FETCH_USER_INVITATION_TOKEN_SUCCESS,
        tokenId,
        entities
    };
}

function fetchUserInvitationFailure(tokenId, error) {
    return {
        type: FETCH_USER_INVITATION_TOKEN_FAILURE,
        tokenId,
        error
    };
}

export function fetchUserInvitationToken(
    tokenId,
    tokenType = "ADMIN_INVITE",
    errorHandlers = defaultErrorHandlers
) {
    return dispatch => {
        dispatch(fetchUserInvitationRequest(tokenId));
        return requestWithoutAuth(
            dispatch,
            http =>
                http.get(`${getApiUrl()}/tokens/${tokenId}?type=${tokenType}`),
            errorHandlers
        )
            .then(res => {
                const { entities, result } = normalize(res.data, tokenSchema);
                return dispatch(fetchUserInvitationSuccess(result, entities));
            })
            .catch(err => {
                dispatch(fetchUserInvitationFailure(tokenId, err));
                throw err;
            });
    };
}

// ----------------------------------------------------------------------------

export const ACCEPT_USER_INVITATION_REQUEST = "ACCEPT_USER_INVITATION_REQUEST";
export const ACCEPT_USER_INVITATION_SUCCESS = "ACCEPT_USER_INVITATION_SUCCESS";
export const ACCEPT_USER_INVITATION_FAILURE = "ACCEPT_USER_INVITATION_FAILURE";

function acceptUserInvitationRequest(tokenId) {
    return {
        type: ACCEPT_USER_INVITATION_REQUEST,
        tokenId
    };
}

function acceptUserInvitationSuccess(tokenId, authData) {
    return {
        type: ACCEPT_USER_INVITATION_SUCCESS,
        tokenId,
        authData
    };
}

function acceptUserInvitationFailure(tokenId, error) {
    return {
        type: ACCEPT_USER_INVITATION_FAILURE,
        tokenId,
        error
    };
}

export function acceptUserInvitation(tokenId, email, password) {
    const data = { email, password };

    return dispatch => {
        dispatch(acceptUserInvitationRequest(tokenId));
        return requestWithoutAuth(dispatch, http =>
            http.post(`${getApiUrl()}/users/accept-invitation/${tokenId}`, data)
        )
            .then(res => {
                return dispatch(acceptUserInvitationSuccess(tokenId, res.data));
            })
            .catch(err => {
                dispatch(acceptUserInvitationFailure(tokenId, err));
                throw err;
            });
    };
}

// ----------------------------------------------------------------------------

export const REQUEST_PASSWORD_RESET_REQUEST = "REQUEST_PASSWORD_RESET_REQUEST";
export const REQUEST_PASSWORD_RESET_SUCCESS = "REQUEST_PASSWORD_RESET_SUCCESS";
export const REQUEST_PASSWORD_RESET_FAILURE = "REQUEST_PASSWORD_RESET_FAILURE";

function requestPasswordResetRequest(email) {
    return {
        type: REQUEST_PASSWORD_RESET_REQUEST,
        email
    };
}

function requestPasswordResetSuccess(email) {
    return {
        type: REQUEST_PASSWORD_RESET_SUCCESS,
        email
    };
}

function requestPasswordResetFailure(email, error) {
    return {
        type: REQUEST_PASSWORD_RESET_FAILURE,
        email,
        error
    };
}

export function requestPasswordReset(email) {
    const data = { email };

    return dispatch => {
        dispatch(requestPasswordResetRequest(email));
        return requestWithoutAuth(dispatch, http =>
            http.post(`${getApiUrl()}/users/forgot-password`, data)
        )
            .then(() => {
                return dispatch(requestPasswordResetSuccess(email));
            })
            .catch(err => {
                dispatch(requestPasswordResetFailure(email, err));
                throw err;
            });
    };
}

// ----------------------------------------------------------------------------

export const RESET_PASSWORD_WITH_TOKEN_REQUEST =
    "RESET_PASSWORD_WITH_TOKEN_REQUEST";
export const RESET_PASSWORD_WITH_TOKEN_SUCCESS =
    "RESET_PASSWORD_WITH_TOKEN_SUCCESS";
export const RESET_PASSWORD_WITH_TOKEN_FAILURE =
    "RESET_PASSWORD_WITH_TOKEN_FAILURE";

function resetPasswordWithTokenRequest(email) {
    return {
        type: RESET_PASSWORD_WITH_TOKEN_REQUEST,
        email
    };
}

function resetPasswordWithTokenSuccess(email) {
    return {
        type: RESET_PASSWORD_WITH_TOKEN_SUCCESS,
        email
    };
}

function resetPasswordWithTokenFailure(email, error) {
    return {
        type: RESET_PASSWORD_WITH_TOKEN_FAILURE,
        email,
        error
    };
}

export function resetPasswordWithToken(tokenId, password) {
    const data = { tokenId, password };

    return dispatch => {
        dispatch(resetPasswordWithTokenRequest(tokenId));

        // Passing noOpErrorHandlers because I don't want the default error handlers. They just display generic error
        // notifications and we have better error messages in this case.
        return requestWithoutAuth(
            dispatch,
            http => http.post(`${getApiUrl()}/users/reset-password`, data),
            noOpErrorHandlers
        )
            .then(res => {
                dispatch(session.userAuthorizationSuccess(res.data));
                return dispatch(resetPasswordWithTokenSuccess(tokenId));
            })
            .catch(err => {
                dispatch(resetPasswordWithTokenFailure(tokenId, err));
                throw err;
            });
    };
}

// ----------------------------------------------------------------------------

export const UPDATE_USER_REQUEST = "UPDATE_USER_REQUEST";
export const UPDATE_USER_SUCCESS = "UPDATE_USER_SUCCESS";
export const UPDATE_USER_FAILURE = "UPDATE_USER_FAILURE";

function updateUserRequest(userId) {
    return {
        type: UPDATE_USER_REQUEST,
        userId
    };
}

function updateUserSuccess(userId, data) {
    const { entities } = normalize(data, userSchema);
    return {
        type: UPDATE_USER_SUCCESS,
        userId,
        entities
    };
}

function updateUserFailure(userId, error) {
    return {
        type: UPDATE_USER_FAILURE,
        userId,
        error
    };
}

export function updateUser(userId, properties) {
    return dispatch => {
        dispatch(updateUserRequest(userId));
        return requestWithAuth(dispatch, http =>
            http.post(`${getApiUrl()}/users/${userId}`, properties)
        )
            .then(({ data }) => dispatch(updateUserSuccess(userId, data)))
            .catch(err => dispatch(updateUserFailure(userId, err)));
    };
}



// WEBPACK FOOTER //
// ./src/modules/user/actions.js